import { useToast } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import { translate } from '../../../i18n';
import { GAME_QUERY_KEY } from './useGetGame';

interface KickParticipantResponse {
    success: boolean;
    message: string;
    /**
     * MAJOR units. What the removed participant had paid and is NOT getting
     * back — this endpoint issues no refund. Was `refundAmount`, which the API
     * could never populate (its refund block is disabled) while this modal told
     * the operator the participant would be refunded.
     */
    unrefundedAmount?: number;
    currency?: string;
}

interface KickParticipantRequest {
    gameId: number;
    participantUserId: string;
}

/**
 * Hook to kick/remove any participant from a game (admin functionality).
 * This endpoint allows admins or game organizers to remove any participant,
 * not just the authenticated user.
 */
export const useKickParticipant = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ gameId, participantUserId }: KickParticipantRequest) => {
            return apiClient.fetchApi<KickParticipantResponse, {}>({
                endpoint: `games/${gameId}/kick-participant/${participantUserId}`,
                method: 'POST',
                body: {},
            });
        },
        onSuccess: (data, { gameId }) => {
            if (data.success) {
                // Money left with the organization is not a success detail to
                // skim past: the operator is the only one who can act on it,
                // and no endpoint refunds it for them.
                const stranded = data.unrefundedAmount
                    ? translate('game.participant_removed_not_refunded', {
                          amount: data.unrefundedAmount.toFixed(2),
                          currency: data.currency ?? '',
                      })
                    : undefined;
                toast({
                    title: translate('game.participant_removed_successfully'),
                    description: stranded ?? data.message,
                    status: stranded ? 'warning' : 'success',
                    duration: stranded ? 10000 : 5000,
                    isClosable: true,
                });

                queryClient.invalidateQueries({ queryKey: GAME_QUERY_KEY(gameId) });
            } else {
                toast({
                    title: translate('game.failed_to_remove_participant'),
                    description: data.message,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        },
        onError: (error: any) => {
            toast({
                title: translate('game.error_removing_participant'),
                description: error?.message || translate('common.unexpected_error'),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        },
    });
};
